import { _ } from "../core/polyglot/polyglot.js";
import { isNotNull } from "../core/helpers/object-util.js";
import {
	lastElementsOfArray,
	padArray,
	unique,
} from "@foretagsplatsen/core-helpers/array";
import { parseFullDate } from "../core/helpers/date-util.js";
import Auditor from "./Auditor.js";
import ConsecutiveMonths from "./period/ConsecutiveMonths.js";
import ContactInfo from "./ContactInfo.js";
import CustomPeriod from "./period/CustomPeriod.js";
import FiscalYear from "./period/FiscalYear.js";
import integrationFactory from "./integration/integrationFactory.js";
import Model from "./Model.js";
import nullPeriod from "./period/nullPeriod.js";
import OiIntegration from "./integration/OiIntegration.js";
import ReportDisclaimer from "./ReportDisclaimer.js";
import SieImportIntegration from "./integration/SieImportIntegration.js";
import Timespan from "./period/Timespan.js";

/**
 * A company with fiscal years.
 *
 * @param {string} id - Company id
 * @param {string} referentialApiId - id of the company in
 *   ReferentialAPI
 * @param {string} referentialApiAgencyId - id of the agency the company
 *   belongs to in ReferentialAPI
 * @param {string} referentialApiCountryId - id of the country the company
 *   belongs to in ReferentialAPI
 * @param {string} businessIdentityCode - Company business identity
 *   code
 * @param {string} name - Company name
 * @param {string} ownerIdentifier - Company owner identifier. It can
 *   be an agency or a user
 * @param {string} typeOfOwner - Type of the owner. It can be an
 *   agency or a user
 * @param {string} companyTypeId - Identifier for the company's type
 *   (see CompanyType). Can be `null` to indicate that the company
 *   uses the Agency's default company type
 * @param {boolean} isLightCompany - True if the company is a light
 *   company
 * @param {string} clientNumber - Client number
 * @param {string} officeAffiliation - Office Affiliation
 * @param {ContactInfo} contactInfo - Information about the company
 *   like address and phone number
 * @param {FiscalYear[]} years - Fiscal years belonging to this
 *   company
 * @param {AbstractIntegration} currentImportIntegration - Current
 *   import solution
 * @param {AbstractIntegration[]} integrations - List of import
 *   solutions already used for this company
 * @param {ReportDisclaimer} [reportDisclaimer] - Report disclaimer of
 *   the company
 * @param {Auditor} [auditor] - Auditor of the company
 * @param {boolean} [isProvisionedByOneClick] - True if the agency is
 *   provisioned by OneClick
 * @param {integer} companyStatus - Status of the company (0 = Active,
 *   1 = Archived)
 */
export default class Company extends Model {
	constructor(spec = {}) {
		super(spec);
		this._years = spec.years || [];
		this._id = spec.id;
		this._referentialApiId = spec.referentialApiId;
		this._referentialApiAgencyId = spec.referentialApiAgencyId;
		this._referentialApiCountryId = spec.referentialApiCountryId;
		this._businessIdentityCode = spec.businessIdentityCode || "";
		this._name = spec.name;
		this._ownerIdentifier = spec.ownerIdentifier;
		this._typeOfOwner = spec.typeOfOwner;
		this._companyTypeId = spec.companyTypeId;
		this._isLightCompany = spec.isLightCompany;
		this._contactInfo = spec.contactInfo;

		this._currentImportIntegration = spec.currentImportIntegration;

		if (!this._currentImportIntegration)
			this.setDefaultCurrentImportIntegration();

		this._integrations = spec.integrations || [
			this._currentImportIntegration,
		];

		this._clientNumber = spec.clientNumber || "";
		this._officeAffiliation = spec.officeAffiliation || "";

		this._reportDisclaimer =
			spec.reportDisclaimer || new ReportDisclaimer();

		this._auditor = spec.auditor || new Auditor();
		this._packageName = spec.packageName || "";

		this._isProvisionedByOneClick = spec.isProvisionedByOneClick;
		this._companyStatus = spec.companyStatus || 0;
	}

	getId() {
		return this._id;
	}

	getReferentialApiId() {
		return this._referentialApiId;
	}

	getReferentialApiAgencyId() {
		return this._referentialApiAgencyId;
	}

	getReferentialApiCountryId() {
		return this._referentialApiCountryId;
	}

	getLegacyId() {
		return this.getId();
	}

	getName() {
		return this._name;
	}

	setName(newName) {
		this._name = newName;
	}

	getType() {
		return this.constructor.getType();
	}

	getContactInfo() {
		return this._contactInfo;
	}

	setContactInfo(newContactInfo) {
		this._contactInfo = newContactInfo;
	}

	getOwnerIdentifier() {
		return this._ownerIdentifier;
	}

	getCompanyTypeId() {
		return this._companyTypeId;
	}

	setCompanyTypeId(newCompanyTypeId) {
		this._companyTypeId = newCompanyTypeId;
	}

	setDefaultCompanyTypeId() {
		this._companyTypeId = null;
	}

	hasDefaultCompanyTypeId() {
		return this._companyTypeId === null;
	}

	getAuditor() {
		return this._auditor;
	}

	getReportDisclaimer() {
		return this._reportDisclaimer;
	}

	getCurrentImportIntegration() {
		return this._currentImportIntegration;
	}

	getYears() {
		return this._years.slice(0);
	}

	setYears(newYears) {
		this._years = newYears;
	}

	getOfficeAffiliation() {
		return this._officeAffiliation;
	}

	setOfficeAffiliation(officeAffiliation) {
		this._officeAffiliation = officeAffiliation;
	}

	getClientNumber() {
		return this._clientNumber;
	}

	setClientNumber(clientNumber) {
		this._clientNumber = clientNumber;
	}

	getNameOrDefault() {
		return this._name || _("<no name>");
	}

	getAgencyId() {
		if (!this.isOwnedByAgency()) {
			throw new Error(
				"We expect the owner of the company to be an agency",
			);
		}
		return this.getOwnerIdentifier();
	}

	getBusinessIdentityCode() {
		return this.constructor.convertCompanyIdToBusinessIdentityCode(
			this._id,
		);
	}

	isOwnedByAgency() {
		return this._id[0] === "a";
	}

	upgradeToStandard() {
		this._isLightCompany = false;
	}

	downgradeToLightCompany() {
		this._isLightCompany = true;
	}

	getBudgets() {
		return this.getActiveYears().reduce(
			(acc, year) => acc.concat(year.getBudgets()),
			[],
		);
	}

	setBudgets(budgets) {
		this.getYears().forEach((year) => {
			year.setBudgets(
				budgets.filter(
					(budget) => budget.getFiscalYearId() === year.getId(),
				),
			);
		});
	}

	getActiveYears() {
		return this.getYears().filter((year) => year.isActive());
	}

	getAccountsForChartOfAccounts(chartOfAccounts) {
		return unique(
			this._years
				.filter(
					(year) =>
						year.getChartOfAccountsId() === chartOfAccounts.getId(),
				)
				.flatMap((year) => year.getAccounts()),
			(account) => account.getNumber(),
		);
	}

	getChartOfAccountsIds() {
		return [
			...new Set(this._years.map((each) => each.getChartOfAccountsId())),
		];
	}

	getYearsWithChartOfAccounts(chartOfAccounts) {
		return this._years.filter(
			(year) => year.getChartOfAccountsId() === chartOfAccounts.getId(),
		);
	}

	setLightCompany(isLight) {
		this._isLightCompany = !!isLight;
	}

	isLightCompany() {
		return this._isLightCompany;
	}

	isProvisionedByOneClick() {
		return this._isProvisionedByOneClick;
	}

	otherIntegrations(availableIntegrations) {
		if (this.isProvisionedByOneClick()) {
			return [new OiIntegration()];
		}
		const companyCurrentIntegration = () =>
			this.getCurrentImportIntegration() || availableIntegrations[0];
		return this.allIntegrations()
			.filter((integration) => integration !== companyCurrentIntegration)
			.filter((integration) =>
				integration.importerCanUpdateExistingCompany(),
			)
			.filter((integration) =>
				availableIntegrations.find(
					(availableIntegration) =>
						availableIntegration.getType() ===
						integration.getType(),
				),
			);
	}

	hasReportDisclaimer() {
		return !this._reportDisclaimer.isEmpty();
	}

	hasFiscalYearsWithDifferentChartOfAccounts() {
		return (
			unique(this._years.map((year) => year.getChartOfAccountsId()))
				.length > 1
		);
	}

	setDefaultCurrentImportIntegration() {
		this._currentImportIntegration = new SieImportIntegration();
	}

	/**
	 * Return true if the receiver has at least one unmapped account within its
	 * fiscal years that have `chartOfAccounts` as chart of accounts.
	 */
	hasUnmappedAccounts(chartOfAccounts) {
		return this.getAccountsForChartOfAccounts(chartOfAccounts).some(
			(account) => !chartOfAccounts.hasAccount(account),
		);
	}

	hasEconomicIntegrationWithAccessGrantToken() {
		return this._integrations.some((integration) => {
			if (integration.isEconomicIntegration()) {
				return integration.hasAccessGrantToken();
			}
			return false;
		});
	}

	hasFortnoxIntegration() {
		return this._integrations.some((integration) =>
			integration.isFortnoxIntegration(),
		);
	}

	hasFortnoxExtendedIntegration() {
		return this._integrations.some((integration) =>
			integration.isFortnoxExtendedIntegration(),
		);
	}

	hasPowerOfficeGoIntegration() {
		return this._integrations.some((integration) =>
			integration.isPowerOfficeGoIntegration(),
		);
	}

	hasIsaasIntegration() {
		return this._integrations.some((integration) =>
			integration.isIsaasIntegration(),
		);
	}

	hasTripletexIntegration() {
		return this._integrations.some((integration) =>
			integration.isTripletexIntegration(),
		);
	}

	hasXledgerIntegration() {
		return this._integrations.some((integration) =>
			integration.isXledgerIntegration(),
		);
	}

	hasQuickbooksIntegration() {
		return this._integrations.some((integration) =>
			integration.isQuickbooksIntegration(),
		);
	}

	getQuickbooksIntegrationAttachmentImportOption() {
		const quickbooksIntegration = this._integrations.find((integration) =>
			integration.isQuickbooksIntegration(),
		);
		return quickbooksIntegration?.shouldImportAttachments();
	}

	getTripletexIntegrationInvoiceImportOption() {
		const tripletexIntegration = this._integrations.find((integration) =>
			integration.isTripletexIntegration(),
		);
		return (
			tripletexIntegration && tripletexIntegration.shouldImportInvoices()
		);
	}

	getFortnoxIntegration() {
		return this._integrations.find((integration) =>
			integration.isFortnoxIntegration(),
		);
	}

	getFortnoxExtendedIntegration() {
		return this._integrations.find((integration) =>
			integration.isFortnoxExtendedIntegration(),
		);
	}

	hasLinkedDocumentIntegration() {
		return this._integrations.some((integration) =>
			integration.canHaveLinkedDocuments(),
		);
	}

	hasInvoiceSupport() {
		return this._integrations.some((integration) =>
			integration.hasInvoiceSupport(),
		);
	}

	removeIntegration(integration) {
		this._integrations = this._integrations.filter(
			(companyIntegration) => companyIntegration !== integration,
		);
	}

	/**
	 * Return a collection of all integrations in use by the company.
	 */
	usedIntegrations() {
		return this._integrations.slice();
	}

	/**
	 * Return a collection of all integrations not in use by the company.
	 */
	unusedIntegrations() {
		const usedIntegrationTypes = new Set(
			this._integrations.map((integration) => integration.getType()),
		);
		return integrationFactory
			.allVisibleIntegrationClasses()
			.filter((klass) => !usedIntegrationTypes.has(klass.getType()))
			.map((Class) => new Class());
	}

	allIntegrations() {
		return this.usedIntegrations().concat(this.unusedIntegrations());
	}

	haveBudgets() {
		return this.getBudgets().length > 0;
	}

	getBudgetsForFiscalYear(fiscalYear) {
		return fiscalYear.getBudgets();
	}

	removeYear(deletedYear) {
		this._years = this._years.filter(
			(year) => year.getId() !== deletedYear.getId(),
		);
	}

	haveYears() {
		return this.getActiveYears().length > 0;
	}

	getCurrentYear() {
		return this.getActiveYears().at(-1) || nullPeriod;
	}

	lastActiveMonth() {
		return this.getCurrentYear().lastActiveMonth();
	}

	// Return array of FiscalYears of size <numYears> filling missing years with null.
	getLatestYears(numYears, year) {
		numYears ||= 5;
		year ||= this.getCurrentYear();

		const yearIndex = this._years.indexOf(year);
		if (yearIndex < 0) {
			throw "Year must be a year in company";
		}

		// grab latest {numYears} from years
		const latestYears = lastElementsOfArray({
			array: this._years,
			numElements: numYears,
			indexOfLastElement: yearIndex,
		});

		// Pad with null elements [null, null, null, ...]
		return padArray(latestYears, numYears, null);
	}

	getLatestPeriods(numberOfPeriods, lastPeriod) {
		return FiscalYear.getLatestPeriods({
			years: this.getActiveYears(),
			numberOfPeriods,
			lastPeriod,
		}).filter(isNotNull);
	}

	getAllPeriods() {
		return this.getActiveYears().flatMap((year) => year.getAllPeriods());
	}

	/**
	 * Returns a timespan containing all active fiscal years, null if there are none
	 */
	getActiveFiscalYearRange() {
		const activeYears = this.getActiveYears();

		if (activeYears.length === 0) {
			return null;
		}

		return new Timespan({
			start: activeYears[0].getStart(),
			end: activeYears.at(-1).getEnd(),
		});
	}

	findYearByName(name) {
		return this.getActiveYears().find((year) => year.getName() === name);
	}

	findYearById(yearId) {
		return this.getActiveYears().find((year) => year.getId() === yearId);
	}

	findYearByDate(date) {
		return this.getActiveYears().find((year) => year.includes(date));
	}

	findPeriodById(periodIdentifier) {
		const periodTypeRegexp = /([a-z]+)/i;
		const match = periodIdentifier.match(periodTypeRegexp);
		if (!match) {
			return nullPeriod;
		}

		const periodType = match[1];
		if (periodType === "Custom") {
			return this._createCustomPeriodFromId(periodIdentifier);
		}

		if (periodType === "ConsecutiveMonths") {
			return this._createConsecutiveMonthsFromId(periodIdentifier);
		}

		let period;

		this.getYears().forEach((year) => {
			// eslint-disable-next-line no-shadow -- bad practice, we should fix this
			const match = year.findPeriodById(periodIdentifier);
			if (!match.isNullPeriod()) {
				period = match;
			}
		});

		return period || nullPeriod;
	}

	findPeriodByDates(startDate, endDate) {
		const period = this.getAllPeriods().find(
			// eslint-disable-next-line no-shadow -- bad practice, we should fix this
			(period) =>
				period.isStartingAt(startDate) && period.isEndingAt(endDate),
		);
		if (period) {
			return period;
		}

		return this._createCustomPeriodFromDates(startDate, endDate);
	}

	toString() {
		return `${this._name} (${this._businessIdentityCode})`;
	}

	isNullCompany() {
		return false;
	}

	getPictureUrl() {
		return "/Client/img/avatar-company-lg@2x.png";
	}

	getPackageName() {
		return this._packageName;
	}

	setPackageName(newPackageName) {
		this._packageName = newPackageName;
	}

	getLastImportDate() {
		return this.getCurrentImportIntegration().getLastImportDate();
	}

	isActive() {
		return this._companyStatus === 0;
	}

	archive() {
		this._companyStatus = 1;
	}

	unarchive() {
		this._companyStatus = 0;
	}

	_getMatchingFields() {
		return [this._name, this._id];
	}

	_serializedProperties() {
		return {
			id: this._id,
			referentialApiId: this._referentialApiId,
			businessIdentityCode: this._businessIdentityCode,
			name: this._name,
			ownerIdentifier: this._ownerIdentifier,
			typeOfOwner: this._typeOfOwner,
			companyTypeId: this._companyTypeId,
			isLightCompany: this._isLightCompany,
			clientNumber: this._clientNumber,
			officeAffiliation: this._officeAffiliation,
			contactInfo: this._contactInfo,
			auditor: this._auditor,
			integrations: this._integrations.map((integration) => ({
				type: integration,
			})),
		};
	}

	_createCustomPeriodFromId(periodIdentifier) {
		const periodRegexp = /custom-(\d{8})-(\d{8})/i;
		const match = periodIdentifier.match(periodRegexp);
		if (!match) {
			throw new Error(
				`Not a valid period identifier: ${periodIdentifier}`,
			);
		}

		const start = parseFullDate(match[1]);
		const end = parseFullDate(match[2]);

		return this._createCustomPeriodFromDates(start, end);
	}

	_createConsecutiveMonthsFromId(periodIdentifier) {
		const periodRegexp = /consecutivemonths-(\d+)_(\d+)-(\d+-?\d+)/i;
		const match = periodIdentifier.match(periodRegexp);
		if (!match) {
			throw new Error(
				`Not a valid period identifier: ${periodIdentifier}`,
			);
		}

		const year = this.findYearByName(match[3]);

		if (!year) return nullPeriod;

		return new ConsecutiveMonths({
			periodIndex: +match[1],
			additionalMonths: +match[2],
			year,
		});
	}

	_createCustomPeriodFromDates(start, end) {
		const startYear = this.findYearByDate(start);
		const endYear = this.findYearByDate(end);

		if (startYear !== endYear) {
			throw new Error("Custom periods can't span over fiscal years");
		}

		return new CustomPeriod({
			year: startYear,
			start,
			end,
		});
	}

	static getType() {
		return "Company";
	}

	static transformMaterializationData(data, context = {}) {
		const yearsData = data.years || [];
		const years = FiscalYear.materializeAll(yearsData, context);

		const integrations = integrationFactory.materializeAll(
			data.integrations,
			{ companyId: data.id },
		);

		const currentIntegration =
			integrations.find((integration) =>
				integration.matchType(data.currentImportIntegrationType),
			) || integrations[0];

		data.integrations = integrations;
		data.currentImportIntegration = currentIntegration;
		data.contactInfo = new ContactInfo(data.contactInfo);
		data.years = years;

		data.reportDisclaimer &&= ReportDisclaimer.materialize(
			data.reportDisclaimer,
		);

		data.auditor = Auditor.materialize(data.auditor);
	}

	static createAgencyAttachedCompanyId(agencyId, businessIdentityCode) {
		return `a-${agencyId}-${businessIdentityCode}`;
	}

	static createStandaloneCompanyId(userId, businessIdentityCode) {
		return `u-${userId}-${businessIdentityCode}`;
	}

	static convertCompanyIdToBusinessIdentityCode(companyId) {
		// The company id have the format of [owner type]-[owner identifier]-[business identity code]
		// and the owner type is one letter and the owner identifier is one word with characters a-z.
		const regexp = /^.-[^-]+-(.+)$/;

		const result = companyId.match(regexp);

		return result[1];
	}
}
